import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';
import { AREAS_QTRO, STATUS } from './constants';

interface Mensagem {
  id: number;
  mensagem: string;
  autor: string;
  data_mensagem: string;
  status: string;
}

interface Arquivo {
  id: number;
  nome_arquivo: string;
  caminho_arquivo: string;
}

interface Ticket {
  id: number;
  tipo: string;
  contexto: string;
  desafio: string;
  solicitacao: string;
  prioridade: string;
  area_qtro: string;
  email: string;
  status: string;
  data_criacao: string;
  data_atualizacao: string;
  arquivos: Arquivo[];
  mensagens: Mensagem[];
}

interface TicketListProps {
  email: string;
  showProtocol?: boolean;
}

export function TicketList({ email, showProtocol = false }: TicketListProps) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtros, setFiltros] = useState({
    status: '',
    area_qtro: '',
    data_inicio: '',
    data_fim: '',
  });
  const [novaMensagem, setNovaMensagem] = useState('');
  const [ticketSelecionado, setTicketSelecionado] = useState<number | null>(null);

  const buscarTickets = async () => {
    if (!email) return;
    
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        email,
        ...(showProtocol ? {} : filtros),
      });

      const response = await fetch(`https://backend.testeswaffle.org/tickets?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao buscar tickets');
      }

      const data = await response.json();
      setTickets(data.data);
    } catch (err) {
      setError('Erro ao buscar tickets. Tente novamente.');
      toast.error('Erro ao buscar tickets');
    } finally {
      setLoading(false);
    }
  };

  const enviarMensagem = async (ticketId: number) => {
    if (!novaMensagem.trim()) return;

    try {
      const response = await fetch(`https://backend.testeswaffle.org/tickets/${ticketId}/mensagens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          mensagem: novaMensagem,
          autor: email,
          status: 'Pendente'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao enviar mensagem');
      }

      setNovaMensagem('');
      buscarTickets(); // Atualiza a lista de tickets para mostrar a nova mensagem
      toast.success('Mensagem enviada com sucesso!');
    } catch (err) {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  useEffect(() => {
    if (email && email.match(/@(waffle\.com\.br|thenewscc\.com\.br)$/)) {
      buscarTickets();
    }
  }, [email, ...(showProtocol ? [] : [filtros])]);

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {!showProtocol && (
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-white/80 p-4 rounded-xl border border-[#1D1D1B]/10">
          <h2 className="text-xl font-bold text-[#1D1D1B]">Seus Tickets</h2>
          
          <div className="flex flex-wrap gap-3">
            <select
              value={filtros.status}
              onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
              className="h-10 rounded-lg border-[0.8px] border-[#1D1D1B]/10 bg-white px-3 text-sm"
            >
              <option value="">Status</option>
              {STATUS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              value={filtros.area_qtro}
              onChange={(e) => setFiltros({ ...filtros, area_qtro: e.target.value })}
              className="h-10 rounded-lg border-[0.8px] border-[#1D1D1B]/10 bg-white px-3 text-sm"
            >
              <option value="">Área QTRO</option>
              {AREAS_QTRO.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>

            <Input
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
              className="h-10 w-auto"
              placeholder="Data Início"
            />

            <Input
              type="date"
              value={filtros.data_fim}
              onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
              className="h-10 w-auto"
              placeholder="Data Fim"
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1B]/20 border-t-[#1D1D1B] rounded-full mx-auto"></div>
          <p className="mt-4 text-[#4D4D4D]">Carregando tickets...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">{error}</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-8 text-[#4D4D4D]">
          Nenhum ticket encontrado
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <Card key={ticket.id} className="bg-white/80 hover:bg-white transition-colors duration-200">
              <CardContent className="p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-[#1D1D1B]">
                        {ticket.tipo} - {ticket.contexto}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-[#4D4D4D]">
                          Criado em {formatarData(ticket.data_criacao)}
                        </p>
                        {showProtocol && (
                          <p className="text-sm font-medium text-pink-500">
                            Protocolo: #{String(ticket.id).padStart(6, '0')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm px-3 py-1 rounded-full bg-[#1D1D1B]/5">
                        {ticket.status}
                      </span>
                      <span className="text-sm">{ticket.prioridade}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[#4D4D4D]"><strong>Desafio:</strong> {ticket.desafio}</p>
                    <p className="text-[#4D4D4D]"><strong>Solicitação:</strong> {ticket.solicitacao}</p>
                  </div>

                  {ticket.arquivos.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-[#1D1D1B]">Arquivos:</p>
                      <div className="flex flex-wrap gap-2">
                        {ticket.arquivos.map((arquivo) => (
                          <a
                            key={arquivo.id}
                            href={arquivo.caminho_arquivo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm px-3 py-1 rounded-lg bg-[#1D1D1B]/5 hover:bg-[#1D1D1B]/10 transition-colors duration-200"
                          >
                            {arquivo.nome_arquivo}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#1D1D1B]">Mensagens</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTicketSelecionado(ticketSelecionado === ticket.id ? null : ticket.id)}
                        className="text-[#4D4D4D] hover:text-[#1D1D1B]"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        {ticket.mensagens.length} mensagens
                      </Button>
                    </div>

                    {ticketSelecionado === ticket.id && (
                      <div className="space-y-4 mt-4">
                        <div className="space-y-3">
                          {ticket.mensagens.map((mensagem) => (
                            <div
                              key={mensagem.id}
                              className={`p-3 rounded-lg ${
                                mensagem.autor === email
                                  ? 'bg-pink-50 ml-auto'
                                  : 'bg-[#1D1D1B]/5'
                              } max-w-[80%]`}
                            >
                              <p className="text-sm text-[#4D4D4D]">{mensagem.mensagem}</p>
                              <div className="flex items-center justify-between mt-2 text-xs text-[#4D4D4D]">
                                <span>{mensagem.autor}</span>
                                <span>{formatarData(mensagem.data_mensagem)}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <Input
                            value={novaMensagem}
                            onChange={(e) => setNovaMensagem(e.target.value)}
                            placeholder="Digite sua mensagem..."
                            className="flex-1"
                          />
                          <Button
                            onClick={() => enviarMensagem(ticket.id)}
                            disabled={!novaMensagem.trim()}
                          >
                            Enviar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
} 